import type { ModifiedScreen } from '../../MapView/map.types';

export type ScreenFilterKey = 'adExchange' | 'publisher' | 'venueType' | 'creativeFormat' | 'resolution';

export type FilterableScreen = Partial<ModifiedScreen>;

export type PickedScreenFilters = Partial<Record<ScreenFilterKey, string[]>>;

type NamedField = { name: string }[] | null | undefined;

const cleanValues = (values: (string | null | undefined)[]): string[] =>
  values.map(value => value?.trim() ?? '').filter(Boolean);

const namesOf = (field: NamedField): string[] => cleanValues((field ?? []).map(item => item.name));

const valuesGetters: Record<ScreenFilterKey, (screen: FilterableScreen) => string[]> = {
  adExchange: screen => cleanValues([screen.adExchange_str]),
  creativeFormat: screen => namesOf(screen.allowed_ad_types),
  publisher: screen => cleanValues([screen.publisher_str]),
  resolution: screen => cleanValues([screen.resolution_str]),
  venueType: screen => namesOf(screen.venue_types)
};

// Same order as the filters panel in the design
export const SCREEN_FILTER_KEYS: ScreenFilterKey[] = [
  'adExchange',
  'publisher',
  'venueType',
  'creativeFormat',
  'resolution'
];

/** Values a screen has for a filter. */
export const getScreenFilterValues = (screen: FilterableScreen, key: ScreenFilterKey): string[] =>
  valuesGetters[key](screen);

/** Unique, sorted values a filter can pick among the given screens. */
export function getScreenFilterOptions(screens: FilterableScreen[], key: ScreenFilterKey): string[] {
  const values = screens.flatMap(valuesGetters[key]);
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

/** OR between the picked values of a filter, AND between filters. */
export function matchesScreenFilters(screen: FilterableScreen, picked: PickedScreenFilters): boolean {
  return SCREEN_FILTER_KEYS.every(key => {
    const pickedValues = picked[key] ?? [];
    return pickedValues.length === 0 || valuesGetters[key](screen).some(value => pickedValues.includes(value));
  });
}
